// Stable Router that can be used in useEffect
// https://github.com/vercel/next.js/issues/18127#issuecomment-1619721701
import Router, { useRouter } from 'next/router';
import {
  Dispatch, SetStateAction, useEffect, useRef, useMemo,
} from 'react';
import { buildSearchParams, buildObjectFromSearchParams } from '@utils/data-fetching/searchParams';
import { ObjectFilterParams } from '@utils/infinite-scrolling/processedServerSideQueries';
import { ParsedUrlQuery } from 'querystring';
import { InfinitePageType } from '@type/swr';
import { extractDynamicRouteParams } from './extractDynamicRouteParams';

const getBaseQueryKey = (key: string) => key.replace(/\[.*$/, '');

/**
 * Generic hook that keeps the browser URL in sync with page number and filter params (Pages Router).
 * Filter changes → shallow `Router.push`; page changes → `window.history.replaceState` (no re-render);
 * back/forward → restores filter state. For job-list slug URLs use `useJobListHistoryAndURL`.
 */
export const useBrowserHistoryAndURL = (
  pageInView: string | number | undefined,
  searchParams: ObjectFilterParams | object | undefined,
  setSearchParams?: Dispatch<SetStateAction<object>>,
  paginationType: InfinitePageType = 'page',
) => {
  const router = useRouter();
  const lastSearchParamsRef = useRef<string | null>(null);
  // Tracks all keys ever in searchParams so removed-filter params aren't treated as "foreign" params.
  const allManagedKeysRef = useRef<Set<string>>(new Set(Object.keys(searchParams || {}).map(getBaseQueryKey)));

  // Track the stable base pathname (the actual page we rendered).
  // Native history.pushState from modals does NOT change router.asPath.
  const basePathnameRef = useRef<string>('');
  useEffect(() => {
    if (router.isReady) {
      const [basePathname] = router.asPath.split('?');
      basePathnameRef.current = basePathname;
    }
  }, [router.asPath, router.isReady]);

  // Dynamic route param names (e.g. [locale], [[...pageNr]]) — must not appear in the query string.
  const dynamicRouteParams = useMemo(
    () => extractDynamicRouteParams(router.pathname),
    [router.pathname],
  );

  useEffect(() => {
    if (!router.isReady) return;
    const { pageNr: pageNrQuery, ...otherQueryParams } = router.query;

    const searchParamsKeys = new Set(Object.keys(searchParams || {}));
    searchParamsKeys.forEach((k) => allManagedKeysRef.current.add(getBaseQueryKey(k)));

    // Extra params from router.query: not dynamic route segments, not filter keys.
    // Strip bracket suffixes (e.g. 'careerLevels[]' → 'careerLevels') — Router.push with a query string
    // causes router.query to contain encoded bracket-keyed entries.
    const routerExtraParams: ParsedUrlQuery = {};
    Object.entries(otherQueryParams).forEach(([key, value]) => {
      const baseKey = getBaseQueryKey(key);
      if (!dynamicRouteParams.has(key) && !allManagedKeysRef.current.has(baseKey)) {
        routerExtraParams[key] = value;
      }
    });

    // Preserve "foreign" params from window.location.search (e.g. selectedCvSlug set by modal components
    // via native history API) — Next.js router never sees them and would silently drop them.
    const liveSearchParams = new URLSearchParams(window.location.search);
    const actualQueryParams: ParsedUrlQuery = { ...routerExtraParams };
    liveSearchParams.forEach((value, key) => {
      const baseKey = getBaseQueryKey(key);
      if (
        key !== 'pageNr'
        && !dynamicRouteParams.has(key)
        && !allManagedKeysRef.current.has(baseKey)
        && !(key in routerExtraParams)
      ) {
        actualQueryParams[key] = value;
      }
    });

    let pageNrQueryValue: string | number = paginationType === 'cursor' ? '' : 1;
    if (paginationType === 'cursor') {
      pageNrQueryValue = Array.isArray(pageNrQuery) ? pageNrQuery[0] : (pageNrQuery ?? '');
    } else if (Array.isArray(pageNrQuery)) {
      pageNrQueryValue = parseInt(pageNrQuery[0], 10);
    } else if (typeof pageNrQuery !== 'undefined') {
      pageNrQueryValue = parseInt(pageNrQuery, 10);
    }

    const urlParams = buildSearchParams(searchParams, '');

    // Filter change → shallow push; decode the "as" URL for human-readable brackets in the address bar.
    if (lastSearchParamsRef.current !== null && urlParams !== lastSearchParamsRef.current) {
      const newQueryWithAllParams: ParsedUrlQuery = { ...actualQueryParams };
      if (typeof pageNrQuery !== 'undefined' && !dynamicRouteParams.has('pageNr')) {
        newQueryWithAllParams.pageNr = pageNrQuery;
      }

      const newQuery = `${urlParams}${buildSearchParams(newQueryWithAllParams, '&')}`;

      // Decode only bracket sequences for a readable address bar; leave & and = encoded to avoid splitting values.
      const decodedQuery = newQuery.replace(/%5B/gi, '[').replace(/%5D/gi, ']');
      const asUrl = `${basePathnameRef.current || window.location.pathname}?${decodedQuery}`;

      // Use the actual resolved path for both internal (href) and external (as) URLs.
      // Passing only { query: string } to Router.push on a dynamic route often fails because required segments are missing.
      void Router.push(asUrl, asUrl, { shallow: true, scroll: false });
    }
    lastSearchParamsRef.current = urlParams;

    // Page / cursor change → replaceState to avoid React re-render (infinite-scroll reset).
    // For 'page' pagination validate numeric; for 'cursor' allow any non-empty string cursor.
    // 'page' compares against the live URL: replaceState never updates router.query, so it goes stale after
    // the first scroll. Page 1 is the URL without pageNr — writing ?pageNr=1 on first load made GA4 (browser
    // history events) count a second page_view for every list load.
    const currentPageNr = liveSearchParams.get('pageNr') ?? String(pageNrQueryValue);
    const pageInViewChanged = paginationType === 'cursor'
      ? (pageInView !== pageNrQueryValue)
      : (!Number.isNaN(Number(pageInView)) && String(pageInView) !== currentPageNr);
    if (pageInViewChanged) {
      const newQueryWithPageNr: ParsedUrlQuery = { ...actualQueryParams };

      if (paginationType === 'cursor') {
        newQueryWithPageNr.pageNr = pageInView?.toString();
      } else if (Number(pageInView) > 1) {
        newQueryWithPageNr.pageNr = pageInView?.toString();
      }

      const newQueryString = `${urlParams}${buildSearchParams(newQueryWithPageNr, '&')}`;
      const base = basePathnameRef.current || window.location.pathname;
      const decodedQueryString = newQueryString.replace(/%5B/gi, '[').replace(/%5D/gi, ']');
      const newUrl = decodedQueryString ? `${base}?${decodedQueryString}` : base;

      window.history.replaceState(
        { ...window.history.state, as: newUrl, url: newUrl },
        '',
        newUrl,
      );
    }
  }, [pageInView, searchParams, router, paginationType, dynamicRouteParams]);


  // Restore filter state on back/forward navigation.
  useEffect(() => {
    router.beforePopState(({ as }) => {
      const currentPath = router.asPath;
      if (as !== currentPath && setSearchParams) {
        const currentParamsObj = buildObjectFromSearchParams(window.location.search) || {};
        setSearchParams(currentParamsObj);
      }
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [router, setSearchParams]);
};
